// Transaction-reference tokenization + matching — the single source of truth for how
// `IRM1501…` references are found in, and matched against, invoice text.
//
// A transaction `reference_number` is generated as `IRM1501` + a zero-padded sequence
// (see the `transactions.reference_number` DEFAULT in schema.sql), so the digit core
// ALWAYS starts `1501` and is at least 8 digits long. Vendors name it on their invoices in
// several forms, all sharing that core:
//   - `IRM15013854`            (canonical, IRM-prefixed)
//   - `Z-IRM15011312313`       (glued to an order/"Zlecenie" prefix)
//   - `15012423432`            (bare digits, IRM prefix dropped)
//
// Matching on the digit CORE (not the `IRM` literal) makes all three forms equivalent.

/** Reference-token patterns recognized in invoice/line text (compiled with `g`).
 *  - `IRM\d+`           — the IRM-prefixed scheme (also matches synthetic short refs in tests).
 *  - `(?<!\d)1501\d{4,}` — the 1501-anchored digit core, so a reference stripped of its `IRM`
 *    prefix (bare digits) is still found; the "not preceded by a digit" guard keeps it out of
 *    other long numbers (NIP, amounts, bank accounts).
 *  Adding a vendor's scheme is a one-line change here (was a hardcoded literal in p1Match). */
export const REF_PATTERNS: readonly string[] = ['IRM\\d+', '(?<!\\d)1501\\d{4,}'];

/** The digit core (e.g. "15013854") of a reference in any form, or null if the text carries
 *  no 1501-anchored reference. Returns the FIRST core found. */
export function referenceCore(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = text.match(/(?<!\d)1501\d{4,}/);
  return m ? m[0] : null;
}

/** Canonical form `IRM<core>` for a reference given in any form, or null. */
export function canonicalReference(text: string | null | undefined): string | null {
  const core = referenceCore(text);
  return core ? `IRM${core}` : null;
}

/** All raw reference tokens in a text, per {@link REF_PATTERNS}. Used to DETECT that a line
 *  names a reference (an explicit ref must not be overridden by a blind P2 amount match).
 *  Tokens are raw — may be IRM-prefixed or bare; use {@link extractReferences} for the
 *  canonical, deduped set. */
export function extractRefs(text: string): string[] {
  const out: string[] = [];
  for (const pat of REF_PATTERNS) {
    const matches = text.match(new RegExp(pat, 'g'));
    if (matches) out.push(...matches);
  }
  return out;
}

/** Every distinct canonical reference (`IRM1501…`) appearing anywhere in a text blob, in
 *  first-seen order. Built on {@link extractRefs} + {@link canonicalReference}: raw tokens →
 *  `IRM<core>`, with non-1501 tokens (e.g. synthetic `IRM001`) dropped. Used by KSeF
 *  ingestion to pull every reference out of the invoice (line text, footer, annotations). */
export function extractReferences(text: string | null | undefined): string[] {
  if (!text) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of extractRefs(text)) {
    const canon = canonicalReference(raw);
    if (canon && !seen.has(canon)) { seen.add(canon); out.push(canon); }
  }
  return out;
}

/** Whether `text` contains `ref` in ANY form. A real reference is matched on its 1501 core,
 *  so `IRM15018031` matches `IRM15018031`, `Z-IRM15018031`, and bare `15018031`; a ref with
 *  no 1501 core (synthetic test refs) is matched literally.
 *
 *  NO leading boundary — the reference is glued onto ALL kinds of preceding tokens in
 *  Fakturownia lines (an order/zlecenie prefix "zlecenie nr ZIRM15017093", the EWC waste
 *  code "Stłuczka szklana 150107IRM15017384", any other word). Trailing boundary is `(?!\d)`
 *  only (NOT `(?!\w)`): the ref is often glued onto the NEXT word ("Z-IRM15017462Usługa"), so
 *  a trailing letter must be allowed; a trailing DIGIT is rejected so a shorter ref can't
 *  match inside a longer number. */
export function containsRef(text: string, ref: string): boolean {
  const needle = referenceCore(ref) ?? ref;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`${escaped}(?!\\d)`, 'i').test(text);
}
